/* ==================================================================
   O roteiro da triagem — Reflect On Engenharia
   ------------------------------------------------------------------
   Só as PERGUNTAS moram aqui. O que cada resposta provoca é decisão de
   `classificar()`, e de mais ninguém.

   Isso não é organização por gosto: a versão anterior carregava em cada
   opção um campo `peso` ('limpo', 'analise', 'recusa_sem_art'), que
   parecia governar o roteamento e não era lido por absolutamente
   ninguém — o roteamento sempre esteve em `classificar()`. Campo inerte
   com cara de configuração é armadilha: mais cedo ou mais tarde alguém
   troca o `peso` de uma opção, testa, não vê efeito nenhum, e perde a
   tarde. Aqui ele virou `nota`, que é o que sempre foi de verdade.
   ================================================================== */

import type { Escopo } from "./tipos";

/** Para onde esta opção TENDE a levar. É documentação para quem lê o
 *  roteiro — o roteamento de verdade está em `classificar()`. Mudar isto
 *  não muda comportamento nenhum. */
export type NotaDeRoteamento =
  | "limpo"
  | "analise"
  | "recusa_sem_art"
  | "recusa_estrutural"
  | "recusa_acompanhamento";

export type Opcao = {
  v: string;
  t: string;
  nota: NotaDeRoteamento;
};

export type Passo = {
  id: "escopo" | "estrutural" | "valor" | "acompanha";
  tipo: "multi" | "unica";
  /** o passo só aparece se um destes escopos estiver marcado */
  sePresente?: Escopo[];
  pergunta: string;
  ajuda: string;
  opcoes: Opcao[];
};

/** O nome legível de um escopo, para telas que mostram o que foi pedido.
 *
 *  Sai daqui e não de um segundo dicionário: o rótulo já existe no roteiro,
 *  e duas listas de nomes divergem no dia em que um escopo entra — como o
 *  gás entrou em 28/08. Escopo desconhecido volta como o próprio código, que
 *  é feio na tela mas honesto, e melhor que sumir da lista em silêncio. */
export function rotuloDoEscopo(codigo: string): string {
  const passo = PASSOS.find((p) => p.id === "escopo");
  return passo?.opcoes.find((o) => o.v === codigo)?.t ?? codigo;
}

export const PASSOS: Passo[] = [
  {
    id: "escopo",
    tipo: "multi",
    pergunta: "O que a sua reforma vai mexer?",
    ajuda: "Pode marcar mais de uma. Se ficar em dúvida, marque: é melhor sobrar informação.",
    opcoes: [
      { v: "piso", t: "Troca de piso ou revestimento", nota: "limpo" },
      { v: "pintura", t: "Pintura, gesso ou marcenaria", nota: "limpo" },
      { v: "ar", t: "Instalação de ar-condicionado", nota: "limpo" },
      { v: "vidro", t: "Envidraçamento de sacada", nota: "limpo" },
      { v: "parede", t: "Remoção ou abertura de parede", nota: "analise" },
      { v: "hidraulica", t: "Mudança de ponto de água ou esgoto", nota: "analise" },
      { v: "eletrica", t: "Mudança na parte elétrica", nota: "analise" },
      /* Gás entrou no escopo em 28/08, depois de o Alan consultar o CREA e
         trazer seis atividades. Viraram um rótulo só: a formulação original
         dele ("gás natural ou aquecedor a gás") deixava o GLP de fora, que é
         comum em prédio com central. */
      { v: "gas", t: "Ponto de gás, tubulação ou aquecedor", nota: "analise" },
      { v: "nenhum", t: "Nada disso, é só decoração", nota: "recusa_sem_art" },
    ],
  },
  {
    id: "estrutural",
    tipo: "unica",
    sePresente: ["parede"],
    pergunta: "A parede que você quer mexer é estrutural?",
    ajuda: "Estrutural é a que sustenta o prédio. Se não souber, tudo bem: a gente confere.",
    opcoes: [
      { v: "nao", t: "Não, é parede de divisória comum", nota: "analise" },
      { v: "sim", t: "Sim, é estrutural", nota: "recusa_estrutural" },
      { v: "naosei", t: "Não sei dizer", nota: "analise" },
    ],
  },
  {
    id: "valor",
    tipo: "unica",
    pergunta: "Quanto a obra vai custar, mais ou menos?",
    ajuda: "É o valor total da reforma, não o da ART. Isso define a faixa da taxa do CREA.",
    opcoes: [
      { v: "ate15", t: "Até R$ 15 mil", nota: "limpo" },
      { v: "acima15", t: "Acima de R$ 15 mil", nota: "analise" },
    ],
  },
  {
    id: "acompanha",
    tipo: "unica",
    pergunta: "Você precisa que o engenheiro acompanhe a obra?",
    ajuda: "Acompanhamento é visitar o local durante a execução. É outro serviço, diferente da ART.",
    opcoes: [
      { v: "nao", t: "Não, só preciso do documento pro síndico", nota: "limpo" },
      { v: "sim", t: "Sim, quero acompanhamento", nota: "recusa_acompanhamento" },
    ],
  },
];
